const TRAILING_COUNTER = /^\d+$/;

/**
 * Reduces an already-numbered prefix to the series it belongs to, so that
 * seeding with a generated name ("New Ad 1") continues that series instead of
 * starting a nested one ("New Ad 1 1").
 *
 * A trailing number is always read as a counter — series start at 1, so the
 * bare name is typically absent and cannot be used to tell a counter apart
 * from a number that is part of the name. The trade-off: copying a name that
 * ends in a meaningful number ("Campaign 2024") continues it ("Campaign 2025").
 */
const toSeriesPrefix = (prefix: string): string => {
  const trimmed = prefix.trimEnd();
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace <= 0 || !TRAILING_COUNTER.test(trimmed.slice(lastSpace + 1))) {
    return trimmed;
  }
  return trimmed.slice(0, lastSpace).trimEnd();
};

const nextCounter = (seriesPrefix: string, names: ReadonlyArray<string>): number => {
  const usedNumbers = names
    .filter((name) => name.startsWith(seriesPrefix))
    .map((name) => {
      const parsed = parseInt(name.substring(seriesPrefix.length).trim(), 10);
      return Number.isNaN(parsed) ? 0 : parsed;
    });
  return Math.max(0, ...usedNumbers) + 1;
};

/**
 * Truncates the prefix so `"{prefix} {counter}"` fits, reserving one separator space plus `digits`
 * digits. Keeps at least one character, so for a `maxLength` below 3 the floor wins over the cap.
 */
const fitToBudget = (seriesPrefix: string, maxLength: number, digits: number): string =>
  seriesPrefix.slice(0, Math.max(1, maxLength - digits - 1)).trimEnd();

/**
 * Returns the next available "{prefix} N" name by parsing trailing numbers
 * in existing names. Picks max(used) + 1; does not fill gaps.
 *
 * Example: with prefix "New Ad" and existing ["New Ad 1", "New Ad 3"] returns "New Ad 4".
 *
 * `maxLength` is the entity's schema name cap: the prefix is truncated to leave room for the
 * counter. Required rather than optional so every caller states its cap — the copy path needs it
 * because the seed is a user-authored name that may already sit at the limit, and the create paths
 * only get away without it because their seeds are short constants today. One character of prefix
 * is always kept, so the cap is best-effort below `maxLength = 3`.
 */
export const nextNewName = (prefix: string, names: ReadonlyArray<string>, maxLength: number): string => {
  const seriesPrefix = toSeriesPrefix(prefix);

  // Truncating the prefix widens the series, which can raise the counter into another digit and eat
  // the room reserved for it. Re-fit until the budget holds; `digits` only grows, so this settles.
  let digits = 1;
  for (;;) {
    const fitted = fitToBudget(seriesPrefix, maxLength, digits);
    const counter = nextCounter(fitted, names);
    const counterDigits = String(counter).length;
    if (counterDigits <= digits) {
      return `${fitted} ${counter}`;
    }
    digits = counterDigits;
  }
};
